import React, { useMemo } from "react";
import { formatCurrency } from "../../../../utils/financialMath";

/**
 * Average Cards Component
 * Displays Average Day, Average Week, Average Month (from last 365 days), and Year to date.
 */
const AverageCards = ({ earnings, earningsYTD }) => {
  const averages = useMemo(() => {
    if (!earnings || earnings.length === 0) {
      return {
        totals: {
          totalBasePay: 0,
          totalTipsCash: 0,
          totalTipsElectronic: 0,
          totalEarnings: 0,
        },
        averageDay: {
          basePay: 0,
          tipsCash: 0,
          tipsElectronic: 0,
          total: 0,
        },
        averageWeek: {
          basePay: 0,
          tipsCash: 0,
          tipsElectronic: 0,
          total: 0,
        },
        averageMonth: {
          basePay: 0,
          tipsCash: 0,
          tipsElectronic: 0,
          total: 0,
        },
        ytd: null,
      };
    }

    // Calculate totals for each earning type
    const totals = earnings.reduce(
      (acc, earning) => {
        acc.totalBasePay += earning.base_pay || 0;
        acc.totalTipsCash += earning.tips_cash || 0;
        acc.totalTipsElectronic += earning.tips_electronic || 0;
        return acc;
      },
      {
        totalBasePay: 0,
        totalTipsCash: 0,
        totalTipsElectronic: 0,
      }
    );

    totals.totalEarnings =
      totals.totalBasePay + totals.totalTipsCash + totals.totalTipsElectronic;

    // Unique performance days (days with at least one show) for "average per day of work"
    const uniqueDates = new Set(
      earnings
        .map((e) => (e.date ? String(e.date).slice(0, 10) : ""))
        .filter(Boolean)
    );
    const performanceDayCount = Math.max(uniqueDates.size, 1);

    // Average per day of work (total ÷ days you actually performed)
    const averageDay = {
      basePay: totals.totalBasePay / performanceDayCount,
      tipsCash: totals.totalTipsCash / performanceDayCount,
      tipsElectronic: totals.totalTipsElectronic / performanceDayCount,
      total: totals.totalEarnings / performanceDayCount,
    };

    // Calculate averages per week
    const averageWeek = {
      basePay: totals.totalBasePay / 52,
      tipsCash: totals.totalTipsCash / 52,
      tipsElectronic: totals.totalTipsElectronic / 52,
      total: totals.totalEarnings / 52,
    };

    // Calculate averages per month
    const averageMonth = {
      basePay: totals.totalBasePay / 12,
      tipsCash: totals.totalTipsCash / 12,
      tipsElectronic: totals.totalTipsElectronic / 12,
      total: totals.totalEarnings / 12,
    };

    return {
      totals,
      averageDay,
      averageWeek,
      averageMonth,
      ytd: null, // computed separately from earningsYTD
    };
  }, [earnings]);

  // Year-to-date totals: sum of earnings from Jan 1 of current year through today (past shows only)
  const ytd = useMemo(() => {
    if (!earningsYTD || earningsYTD.length === 0) {
      return { basePay: 0, tipsCash: 0, tipsElectronic: 0, total: 0 };
    }
    const sum = earningsYTD.reduce(
      (acc, e) => {
        acc.basePay += e.base_pay || 0;
        acc.tipsCash += e.tips_cash || 0;
        acc.tipsElectronic += e.tips_electronic || 0;
        return acc;
      },
      { basePay: 0, tipsCash: 0, tipsElectronic: 0 }
    );
    sum.total = sum.basePay + sum.tipsCash + sum.tipsElectronic;
    return sum;
  }, [earningsYTD]);

  const displayYtd = averages.ytd === null ? ytd : averages.ytd;

  const tooltipDay =
    "This is your average pay on days you actually performed. We take your total earnings from the past year and divide by the number of days you had at least one show.";
  const tooltipWeek =
    "Your total earnings from the past year, divided by 52 weeks. This shows what you typically earn in a week.";
  const tooltipMonth =
    "Your total earnings from the past year, divided by 12 months. This shows what you typically earn in a month.";
  const tooltipYTD =
    "Your total earnings from January 1st of this year through today. Only past shows are included. Base pay, cash tips, and electronic tips from each show are added up for this period.";

  return (
    <div className="financial-grid">
      <div className="financial-card">
        <span
          className="info-icon card-info-icon"
          data-tooltip={tooltipDay}
          aria-label="How this number is calculated"
        >
          ?
        </span>
        <h3>Average Day</h3>
        <div className="earnings-breakdown">
          <div className="time-slot">
            <span className="slot-label">Base Pay</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageDay.basePay)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Cash Tips</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageDay.tipsCash)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Electronic Tips</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageDay.tipsElectronic)}
            </span>
          </div>
          <div className="time-slot total">
            <span className="slot-label">Total</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageDay.total)}
            </span>
          </div>
        </div>
      </div>

      <div className="financial-card">
        <span
          className="info-icon card-info-icon"
          data-tooltip={tooltipWeek}
          aria-label="How this number is calculated"
        >
          ?
        </span>
        <h3>Average Week</h3>
        <div className="earnings-breakdown">
          <div className="time-slot">
            <span className="slot-label">Base Pay</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageWeek.basePay)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Cash Tips</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageWeek.tipsCash)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Electronic Tips</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageWeek.tipsElectronic)}
            </span>
          </div>
          <div className="time-slot total">
            <span className="slot-label">Total</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageWeek.total)}
            </span>
          </div>
        </div>
      </div>

      <div className="financial-card">
        <span
          className="info-icon card-info-icon"
          data-tooltip={tooltipMonth}
          aria-label="How this number is calculated"
        >
          ?
        </span>
        <h3>Average Month</h3>
        <div className="earnings-breakdown">
          <div className="time-slot">
            <span className="slot-label">Base Pay</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageMonth.basePay)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Cash Tips</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageMonth.tipsCash)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Electronic Tips</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageMonth.tipsElectronic)}
            </span>
          </div>
          <div className="time-slot total">
            <span className="slot-label">Total</span>
            <span className="slot-amount">
              {formatCurrency(averages.averageMonth.total)}
            </span>
          </div>
        </div>
      </div>

      <div className="financial-card">
        <span
          className="info-icon card-info-icon"
          data-tooltip={tooltipYTD}
          aria-label="How this number is calculated"
        >
          ?
        </span>
        <h3>Year to date</h3>
        <div className="earnings-breakdown">
          <div className="time-slot">
            <span className="slot-label">Base Pay</span>
            <span className="slot-amount">
              {formatCurrency(displayYtd.basePay)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Cash Tips</span>
            <span className="slot-amount">
              {formatCurrency(displayYtd.tipsCash)}
            </span>
          </div>
          <div className="time-slot">
            <span className="slot-label">Electronic Tips</span>
            <span className="slot-amount">
              {formatCurrency(displayYtd.tipsElectronic)}
            </span>
          </div>
          <div className="time-slot total">
            <span className="slot-label">Total</span>
            <span className="slot-amount">
              {formatCurrency(displayYtd.total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AverageCards;
